/* eslint-disable max-lines */
import React from 'react';
import { Line } from 'react-chartjs-2';
import { ChartOptions, ChartData, ScriptableContext } from 'chart.js';
import { useTheme } from 'providers/ThemeProvider';
import 'chartjs-adapter-moment';
import utils from 'utils';
import { AXIS_TYPE } from 'utils/constants';
import { generateChartColors } from 'utils/helpers/colors.helpers';

type Value = { x: string; y: number; formattedY: string };

export interface AreaChartProps {
  colors?: string[];
  datasets: {
    axisFormatTypes: { x: string; y: string };
    data: Value[];
    label: string;
    name?: string;
  }[];
  percentage?: boolean;
}

const AreaChart = ({ colors, datasets, percentage }: AreaChartProps) => {
  const { theme, isDarkMode } = useTheme();

  // If we have exactly 2 datasets, we fill the area between them. Otherwise, we just show them as lines.
  const isExactlyTwoDatasets = datasets.length === 2;

  const chartColors = colors ?? utils.colors.generateChartColors(datasets.length, theme.colorPalette.graphics);
  const setOpacity = (color: string, alpha: number) => {
    if (color?.startsWith('rgba')) {
      return color.replace(/[\d|.]+\)$/g, `${alpha})`);
    }
    if (color?.startsWith('#')) {
      return `${color}${Math.floor(alpha * 255)
        .toString(16)
        .padStart(2, '0')}`;
    }
    if (color?.startsWith('hsl')) {
      return color.replace('hsl', 'hsla').replace(')', `, ${alpha})`);
    }
    return color;
  };

  const createGradient = (context: ScriptableContext<'line'>) => {
    const ctx = context.chart.ctx;
    const chartArea = context.chart.chartArea;

    if (!chartArea) {
      return 'transparent';
    }

    const color1 = chartColors[0] || generateChartColors(1, theme.colorPalette.graphics)[0];
    const color2 = chartColors[1] || generateChartColors(1, theme.colorPalette.graphics)[1];

    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, setOpacity(color1, 0.4));
    gradient.addColorStop(1, setOpacity(color2, 0.1));

    return gradient;
  };

  const isDateFormat = datasets.every((dataset) => dataset.axisFormatTypes.x === AXIS_TYPE.date);
  const isDateMonth = datasets.every((dataset) => dataset.axisFormatTypes.x === AXIS_TYPE.dateMonth);

  const formattedDatasets = datasets.map((dataset, index) => {
    const color = chartColors[index];
    const convertDatasetDates = dataset.data.map(({ x, y }) => {
      const convertedX = utils.date.convertDdMmYyyyToMmDdYyyy(x);
      return { x: convertedX || x, y };
    });

    const isFirstDataset = index === 0;

    return {
      ...dataset,
      data: (isDateFormat || isDateMonth ? convertDatasetDates : dataset.data) as any,
      borderColor: color,
      backgroundColor: isFirstDataset && isExactlyTwoDatasets ? createGradient : 'transparent',
      // The first dataset fills to the second dataset (index 1), assuming it's 'above' or simply defining the area
      fill: isFirstDataset && isExactlyTwoDatasets ? 1 : false,
      pointBackgroundColor: color,
      pointBorderColor: color,
      pointBorderWidth: 1,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: theme.colorPalette.white,
      pointHoverBorderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 4,
      tension: 0.4,
    };
  });

  const data: ChartData<'line'> = {
    datasets: formattedDatasets,
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ...(isDateFormat || isDateMonth ? { type: 'time' } : {}),
        time: {
          minUnit: isDateMonth ? 'month' : 'day',
          displayFormats: {
            day: isDateMonth ? 'MM/YYYY' : 'DD/MM/YYYY',
          },
        },
        grid: {
          display: false,
        },
        offset: false, // Usually false for area charts to span fully
        ticks: {
          padding: 20,
          color: isDarkMode ? theme.colorPalette.grey.grey7 : theme.colorPalette.grey.grey8,
          font: {
            size: 11,
            family: theme.fontFamily,
            weight: 'lighter',
          },
        },
      },
      y: {
        max: percentage ? 100 : undefined,
        min: percentage ? 0 : undefined,
        grid: {
          color: isDarkMode ? theme.colorPalette.grey.grey3 : theme.colorPalette.grey.grey2,
        },
        ticks: {
          callback: (value) => (percentage ? String(value) + ' %' : value),
          color: isDarkMode ? theme.colorPalette.grey.grey7 : theme.colorPalette.grey.grey8,
          font: {
            size: 9,
            family: theme.fontFamily,
            weight: 'lighter',
          },
        },
      },
    },
    plugins: {
      legend: {
        align: 'start',
        labels: {
          boxWidth: 20,
          boxHeight: 10,
          padding: 15,
          color: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey8,
          font: {
            size: 13,
            family: theme.fontFamily,
            weight: 'lighter',
          },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: () => '', // Hide title if standard behavior
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${percentage ? value + '%' : value}`;
          },
        },
        bodyFont: {
          size: 12,
          weight: '400',
        },
        caretPadding: 10,
        borderColor: isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey3,
        borderWidth: 1,
        backgroundColor: isDarkMode ? theme.colorPalette.grey.grey2 : theme.colorPalette.white,
        titleColor: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.turquoise.dark,
        bodyColor: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.turquoise.dark,
      },
      // Using generic filler plugin behavior
      filler: {
        propagate: false,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  return <Line data={data} options={options} />;
};

export default AreaChart;
